// src/pages/LoginPage.jsx
import { useState } from "react";
import "../styles/login.css";

import jadeLogo from "../assets/jade.png"; // Jade Global logo
import sowLogo from "../assets/sowlogo.png"; // center banner/logo

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";
const IS_DEV = import.meta.env.VITE_DEV_MODE === "true" || import.meta.env.DEV;

export default function LoginPage() {
  const [devEmail, setDevEmail] = useState("");
  const [devError, setDevError] = useState("");

  const handleDevLogin = async () => {
    setDevError("");
    if (!devEmail) {
      setDevError("Enter your email.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/auth/dev/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: devEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDevError(data.detail || "Login failed.");
        return;
      }
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("last_login_email", data.user.email);
      window.location.href = "/";
    } catch {
      setDevError("Could not reach backend.");
    }
  };

  // Check for deactivation or SSO error message
  const loginError = localStorage.getItem("login_error") || "";
  // Clear after reading so it only shows once
  if (loginError) localStorage.removeItem("login_error");

  // Check URL params for SSO redirect errors (e.g. ?error=deactivated)
  const urlParams = new URLSearchParams(window.location.search);
  const urlError = urlParams.get("error") || "";

  const displayError = loginError || urlError;

  // Try in this order:
  // 1) last_login_email (set by SsoCallback)
  // 2) user.email (from last session)
  // 3) default placeholder
  let lastEmail = localStorage.getItem("last_login_email");

  if (!lastEmail) {
    try {
      const userRaw = localStorage.getItem("user");
      if (userRaw) {
        const user = JSON.parse(userRaw);
        if (user?.email) {
          lastEmail = user.email;
        }
      }
    } catch {
      // ignore JSON errors
    }
  }

  if (!lastEmail) {
    lastEmail = "user@jadeglobal.com";
  }

  const initials = lastEmail ? lastEmail[0].toUpperCase() : "U";

  const handleLogin = () => {
    window.location.href = `${API_BASE}/auth/google/login`; // or `/api/auth/google/login` depending on your base
  };

  return (
    <div className="sow-page">
      {/* FULL-WIDTH HEADER */}
      <header className="sow-login-header">
        <div className="sow-header-inner">
          <div className="sow-header-left">
            <img src={jadeLogo} alt="Jade Global" className="sow-jade-logo" />
            <div className="sow-header-title-block">
              <div className="sow-header-title">Jade Orion</div>
              <div className="sow-header-subtitle">
                Unified Intelligent System for end-to-end software delivery
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* BODY: dark strip + centered card */}
      <div className="sow-login-body">
        <aside className="sow-login-sidebar" />
        <main className="sow-login-main">
          <div className="sow-login-card">
            <div className="sow-auth-logo">
              {/* optional logo image */}
              {/* <img src={sowLogo} alt="SOW Analyzer" className="sow-auth-img" /> */}
            </div>

            <h1 className="sow-auth-title">Sign in with Google</h1>
            <p className="sow-auth-subtitle">
              Use your <strong>@jadeglobal.com</strong> Google account to
              continue.
            </p>

            {displayError && (
              <div className="sow-login-error">{displayError}</div>
            )}

            <button className="sow-google-btn" onClick={handleLogin}>
              <div className="sow-google-left">
                <div className="sow-google-avatar">{initials}</div>
                <div className="sow-google-text">
                  <span className="sow-google-label">Sign in as</span>
                  <span className="sow-google-email">{lastEmail}</span>
                </div>
              </div>

              <div className="sow-google-icon">
                <span className="g-seg g-red" />
                <span className="g-seg g-yellow" />
                <span className="g-seg g-green" />
                <span className="g-seg g-blue" />
              </div>
            </button>

            <p className="sow-auth-footnote">
              Access is restricted to Jade Global employees. Your identity is
              verified via Google SSO.
            </p>

            {IS_DEV && (
              <div
                style={{
                  marginTop: "24px",
                  borderTop: "1px dashed #555",
                  paddingTop: "16px",
                }}
              >
                <p
                  style={{
                    fontSize: "11px",
                    color: "#aaa",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Dev login (local only)
                </p>
                <input
                  type="email"
                  placeholder="your@jadeglobal.com"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleDevLogin()}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #444",
                    background: "#1e1e1e",
                    color: "#fff",
                    fontSize: "13px",
                    marginBottom: "8px",
                    boxSizing: "border-box",
                  }}
                />
                {devError && (
                  <p
                    style={{
                      color: "#f87171",
                      fontSize: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    {devError}
                  </p>
                )}
                <button
                  onClick={handleDevLogin}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#374151",
                    color: "#fff",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Login as this user
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
